import { ESectionTrackStatus } from '@constants';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ItemWrapper, TitleWrapper, Wrapper } from './StudySummary.styles';

import VECTOR from '@/assets/study_status_arrow_vector.svg';
import { useIsMobile } from '@/hooks';
import { useCourseDetail, useMySubscriptions } from '@/store';

export const StudySummary = () => {
  const { t } = useTranslation();
  const { selectedSubscription } = useMySubscriptions();
  const { course } = useCourseDetail(selectedSubscription?.course?._id);
  const isMobile = useIsMobile('lg');

  const activeSectionsLength = useMemo(
    () => course?.sections?.filter(s => s.active).length ?? 0,
    [course]
  );

  const calculateSectionPercentage = (status: ESectionTrackStatus) => {
    return (
      ((selectedSubscription?.sectionTracking?.filter(s => s.status === status).length ?? 0) /
        activeSectionsLength) *
      100
    );
  };

  const seenSections = useMemo(
    () => calculateSectionPercentage(ESectionTrackStatus.SEEN),
    [selectedSubscription]
  );

  const summarisedSections = useMemo(
    () => calculateSectionPercentage(ESectionTrackStatus.SUMMARISED),
    [selectedSubscription]
  );
  const studiedSections = useMemo(
    () => calculateSectionPercentage(ESectionTrackStatus.STUDIED),
    [selectedSubscription]
  );
  const revisedSections = useMemo(
    () => calculateSectionPercentage(ESectionTrackStatus.REVISED),
    [selectedSubscription]
  );
  const knownSections = useMemo(
    () => calculateSectionPercentage(ESectionTrackStatus.KNOWN),
    [selectedSubscription]
  );
  const notStartedSections = useMemo(() => {
    const trackingLength = selectedSubscription?.sectionTracking?.length ?? 0;
    const notStatusPercentage =
      ((activeSectionsLength - trackingLength) / activeSectionsLength) * 100;
    return calculateSectionPercentage(ESectionTrackStatus.NOT_STARTED) + notStatusPercentage;
  }, [selectedSubscription]);

  const VectorImage = () => {
    if (isMobile) return null;
    return <img src={VECTOR} alt="vector" />;
  };

  return (
    <Wrapper>
      <ItemWrapper>
        <TitleWrapper>{notStartedSections.toFixed(1)}%</TitleWrapper>
        <span>{t(ESectionTrackStatus.NOT_STARTED)}</span>
      </ItemWrapper>
      <VectorImage />
      <ItemWrapper>
        <TitleWrapper>{seenSections.toFixed(1)}%</TitleWrapper>
        <span>{t(ESectionTrackStatus.SEEN)}</span>
      </ItemWrapper>
      <VectorImage />
      <ItemWrapper>
        <TitleWrapper>{summarisedSections.toFixed(1)}%</TitleWrapper>
        <span>{t(ESectionTrackStatus.SUMMARISED)}</span>
      </ItemWrapper>
      <VectorImage />
      <ItemWrapper>
        <TitleWrapper>{studiedSections.toFixed(1)}%</TitleWrapper>
        <span>{t(ESectionTrackStatus.STUDIED)}</span>
      </ItemWrapper>
      <VectorImage />
      <ItemWrapper>
        <TitleWrapper>{revisedSections.toFixed(1)}%</TitleWrapper>
        <span>{t(ESectionTrackStatus.REVISED)}</span>
      </ItemWrapper>
      <VectorImage />
      <ItemWrapper>
        <TitleWrapper>{knownSections.toFixed(1)}%</TitleWrapper>
        <span>{t(ESectionTrackStatus.KNOWN)}</span>
      </ItemWrapper>
    </Wrapper>
  );
};
